import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Search, FolderOpen, Plus, X, Tag, Users, User as UserIcon, ArrowLeft,
  Code2, Smartphone, Server, Bot, GitBranch, Sun, Moon, Terminal, Coins, Shield, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const typeIcons = { fullstack: Code2, mobile: Smartphone, service: Server, ai_agent: Bot, pipeline: GitBranch };
const typeLabels = { fullstack: 'Full Stack', mobile: 'Mobile', service: 'Service', ai_agent: 'AI Agent', pipeline: 'Pipeline' };

export default function ProjectsListPage() {
  const { user, logout, theme, setTheme } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [scope, setScope] = useState('all'); // 'all' | 'mine' | 'shared'
  const [editingTagsFor, setEditingTagsFor] = useState(null);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search, scope]);

  const reload = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        projectAPI.list({ search: search || undefined, scope }),
        projectAPI.listAllTags(),
      ]);
      setProjects(pRes.data || []);
      setTags(tRes.data || []);
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  // Client-side multi-tag filtering (server supports one tag; we filter further if multiple selected)
  const filteredProjects = useMemo(() => {
    if (activeTags.length === 0) return projects;
    return projects.filter(p => {
      const ptags = p.tags || [];
      return activeTags.every(t => ptags.includes(t));
    });
  }, [projects, activeTags]);

  const toggleTag = (tag) => {
    setActiveTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };

  const clearFilters = () => {
    setActiveTags([]);
    setSearch('');
    setScope('all');
  };

  const openTagEditor = (projectId, currentTags) => {
    setEditingTagsFor(projectId);
    setTagInput((currentTags || []).join(', '));
  };

  const saveTags = async (projectId) => {
    const newTags = tagInput.split(',').map(t => t.trim()).filter(Boolean);
    try {
      await projectAPI.update(projectId, { tags: newTags });
      toast.success('Tags updated');
      setEditingTagsFor(null);
      setTagInput('');
      reload();
    } catch {
      toast.error('Failed to update tags');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="projects-list-page">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Terminal className="w-5 h-5 text-indigo-400" />
              <span className="text-base font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>BuilderShift.ai</span>
            </div>
            <div className="hidden md:flex items-center gap-1 text-sm">
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">Dashboard</Button>
              <Button variant="ghost" size="sm" className="text-indigo-400 hover:text-indigo-300" onClick={() => navigate('/projects')} data-testid="nav-projects">Projects</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/tokens')} data-testid="nav-tokens">Tokens</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/logs')} data-testid="nav-logs">Logs</Button>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white" onClick={() => navigate('/vscode')} data-testid="nav-vscode">VS Code</Button>
              {user?.is_admin && (
                <Button variant="ghost" size="sm" className="text-indigo-400 hover:text-indigo-300" onClick={() => navigate('/admin')} data-testid="nav-admin">
                  <Shield className="w-3.5 h-3.5 mr-1" /> Admin
                </Button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <div className="flex items-center gap-2 bg-white/5 rounded-md px-3 py-1.5 border border-white/10">
              <Coins className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-white">{user?.token_balance || 0}</span>
            </div>
            <Button variant="ghost" size="sm" className="text-white/50" onClick={logout}>Sign Out</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Projects</h1>
            <p className="text-sm text-white/50 mt-1">All your projects, plus those shared with you. Filter by tags, search by name.</p>
          </div>
          <Button onClick={() => navigate('/new-project')} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="new-project-btn">
            <Plus className="w-4 h-4 mr-2" /> New Project
          </Button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by project name..."
                className="pl-10 bg-black/40 border-white/10 text-white"
                data-testid="search-input"
              />
            </div>

            {/* Scope toggle */}
            <div className="flex bg-black/40 border border-white/10 rounded-md p-1">
              {[
                { key: 'all', label: 'All', icon: FolderOpen },
                { key: 'mine', label: 'Mine', icon: UserIcon },
                { key: 'shared', label: 'Shared', icon: Users },
              ].map(opt => {
                const I = opt.icon;
                const active = scope === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => setScope(opt.key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                      active ? 'bg-indigo-500 text-white' : 'text-white/60 hover:text-white'
                    }`}
                    data-testid={`scope-${opt.key}`}
                  >
                    <I className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {(search || activeTags.length > 0 || scope !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-white/50" data-testid="clear-filters-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>

          {/* Tag chips */}
          {tags.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-white/40 flex items-center gap-1"><Filter className="w-3 h-3" /> Tags:</span>
                {tags.map(t => {
                  const active = activeTags.includes(t.tag);
                  return (
                    <button
                      key={t.tag}
                      onClick={() => toggleTag(t.tag)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? 'bg-indigo-500 text-white border-indigo-500'
                          : 'bg-white/5 text-white/60 border-white/10 hover:border-indigo-500/50'
                      }`}
                      data-testid={`tag-chip-${t.tag}`}
                    >
                      <Tag className="w-3 h-3 inline mr-1" />
                      {t.tag} <span className="opacity-50 ml-1">({t.count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Project Grid */}
        {loading ? (
          <div className="text-center py-12 text-white/40">Loading...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-16 bg-white/5 border border-dashed border-white/10 rounded-xl" data-testid="empty-state">
            <FolderOpen className="w-12 h-12 mx-auto mb-3 text-white/20" />
            <p className="text-white/50">No projects match the current filters</p>
            <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4 border-white/20 text-white">Clear filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {filteredProjects.map(p => {
              const TypeIcon = typeIcons[p.project_type] || Code2;
              return (
                <Card key={p.id} className="bg-white/5 border-white/10 hover:border-indigo-500/40 transition-colors cursor-pointer group" data-testid={`project-card-${p.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0" onClick={() => navigate(`/project/${p.id}`)}>
                        <div className="p-2 rounded-lg bg-indigo-500/10">
                          <TypeIcon className="w-4 h-4 text-indigo-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base font-semibold text-white truncate group-hover:text-indigo-300 transition-colors">{p.name}</h3>
                          <p className="text-[10px] text-white/40">{typeLabels[p.project_type]}</p>
                        </div>
                      </div>
                      {p.is_owner ? (
                        <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-400">Owner</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] border-sky-500/30 text-sky-400">Shared</Badge>
                      )}
                    </div>

                    {p.description && (
                      <p className="text-xs text-white/50 line-clamp-2 mb-3" onClick={() => navigate(`/project/${p.id}`)}>{p.description}</p>
                    )}

                    {/* Tags */}
                    {editingTagsFor === p.id ? (
                      <div className="space-y-2 mb-3">
                        <Input
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          placeholder="tag1, tag2, tag3..."
                          className="text-xs bg-black/40 border-white/10 text-white h-8"
                          autoFocus
                          data-testid={`tag-input-${p.id}`}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" className="h-7 text-xs" onClick={() => saveTags(p.id)} data-testid={`save-tags-${p.id}`}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-white/50" onClick={() => { setEditingTagsFor(null); setTagInput(''); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap mb-3 min-h-[24px]">
                        {(p.tags || []).map(t => (
                          <Badge key={t} variant="outline" className="text-[9px] border-indigo-500/30 text-indigo-300 bg-indigo-500/10">
                            <Tag className="w-2.5 h-2.5 mr-1" />{t}
                          </Badge>
                        ))}
                        {p.is_owner && (
                          <button
                            onClick={() => openTagEditor(p.id, p.tags)}
                            className="text-[10px] text-white/40 hover:text-white/80 border border-white/10 rounded-full px-2 py-0.5 hover:border-indigo-500/50"
                            data-testid={`add-tags-${p.id}`}
                          >
                            + Tag
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex items-center justify-between text-[10px] text-white/40">
                      <span>{(p.member_count || 0) > 0 ? `${p.member_count} member${p.member_count > 1 ? 's' : ''}` : 'No members'}</span>
                      <span>{new Date(p.updated_at || p.created_at).toLocaleDateString()}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
